import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../../../store/store';
import { fetchProducts } from '../../../store/ProductSlice';
import type { Product, PromoKey } from '../../../types/common';
import Action from '../../shared/Action/Action';
import ActionLinkButton from '../../UI/ActionLinkButton/ActionLinkButton';
import ProductCard, { type ProductCardData, type Discount, type ImagesObject } from '../../shared/ProductCard/ProductCard';
// import ProductCard, { type ProductCardData, type Discount, type ImagesObject } from '../../ProductSlugPage/components/promoProducts/ProductCard/ProductCard';
// import { Link } from 'react-router-dom';
import { PATHS } from '../../../constants/routes';
import './promoProducts.scss';

type PromoProductsProps = {
    logSampleSize?: number;
    isMobile?: boolean;
};

type PromoProduct = ProductCardData & { promoType: PromoKey };

const isPositiveNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
const toDiscount = (d: unknown): Discount => {
    if (d && typeof d === 'object') {
        const obj = d as { percent?: unknown; discountedPrice?: unknown };
        const percent = isPositiveNumber(obj.percent) ? obj.percent : null;
        const discountedPrice = isPositiveNumber(obj.discountedPrice) ? obj.discountedPrice : null;
        if (percent !== null && discountedPrice !== null) {
            return { percent, discountedPrice };
        }
    }
    return null;
};

const toImages = (p: Product): ImagesObject => ({
    image: p.image ?? '',
    secondImage: p.secondImage ?? '',
    thirdImage: p.thirdImage ?? '',
    fourthImage: p.fourthImage ?? '',
    fifthImage: p.fifthImage ?? '',
    sixthImage: p.sixthImage ?? '',
});

const isPromoKey = (v: unknown): v is PromoKey => v === 'promo' || v === 'season' || v === 'sale' || v === 'blank';
const getPromoType = (p: Product): PromoKey | null => {
    const val = (p as unknown as { promoType?: unknown }).promoType;
    return isPromoKey(val) ? val : null;
};

const mapProductToPromo = (p: Product, discount: Discount, images: ImagesObject, promoType: PromoKey): PromoProduct => {
    return {
        _id: p.id ?? '',
        category: {
            id: p.category?.id ?? '',
            name: p.category?.name ?? '',
            url: p.category?.url ?? '',
        },
        discount,
        name: p.name ?? '',
        price: typeof p.price === 'number' ? p.price : 0,
        weight: typeof p.weight === 'number' && Number.isFinite(p.weight) ? p.weight : 0,
        fillerType: {
            id: p.fillerType?.id ?? '',
            name: p.fillerType?.name ?? '',
            url: p.fillerType?.url ?? '',
        },
        fabricType: {
            id: p.fabricType?.id ?? '',
            name: p.fabricType?.name ?? '',
            url: p.fabricType?.url ?? '',
        },
        articleNumber: p.articleNumber ?? '',
        brandName: p.brandName ?? '',
        nameDescription: p.nameDescription ?? '',
        priority: typeof p.priority === 'number' ? p.priority : 0,
        images,
        group: {
            id: p.group?.id ?? '',
            name: p.group?.name ?? '',
            url: p.group?.url ?? '',
            optionValue: p.group?.optionValue ?? '',
        },
        slug: p.slug ?? '',
        season: p.season ?? '',
        promoType,
    };
};

const makeSelectPromo = (maxSample: number) =>
    createSelector([(s: RootState) => s.product.products], (products): { totalPromo: number; sample: PromoProduct[]; all: PromoProduct[] } => {
        let totalPromo = 0;
        const sample: PromoProduct[] = [];
        const all: PromoProduct[] = [];
        for (const p of products) {
            const disc = toDiscount((p as unknown as { discount?: unknown }).discount);
            const images = toImages(p);
            const promoType = getPromoType(p);
            if (promoType === null) continue;
            totalPromo += 1;
            const narrowed = mapProductToPromo(p, disc, images, promoType);
            all.push(narrowed);
            if (sample.length < maxSample) sample.push(narrowed);
        }
        return { totalPromo, sample, all };
    });

const PromoProducts: React.FC<PromoProductsProps> = ({ logSampleSize = 10, isMobile }) => {
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const isLoading = useSelector((s: RootState) => s.product.isLoading);
    const productsLen = useSelector((s: RootState) => s.product.products.length);
    const [promoProducts, setPromoProducts] = useState<PromoProduct[]>([]);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);
    const [isScrollable, setIsScrollable] = useState(false);
    // const [isDesktop, setIsDesktop] = useState<boolean>(true);

    const cardsClassName = useMemo(() => {
        const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
        const isHome = normalizedPath === '/';
        return `promoProducts__cards${isHome ? '' : ' promoProducts__cards_other'}`;
    }, [location.pathname]);

    useEffect(() => {
        if (!isLoading && productsLen === 0) {
            void dispatch(fetchProducts());
        }
    }, [dispatch, isLoading, productsLen]);

    const selectPromo = useMemo(() => makeSelectPromo(Math.max(0, Math.floor(logSampleSize))), [logSampleSize]);
    const { sample } = useSelector(selectPromo);

    // useEffect(() => {
    //     if (!isLoading) {
    //         setPromoProducts(sample);
    //     }
    // }, [sample, isLoading]);

    useEffect(() => {
        if (sample.length > 0) setPromoProducts(sample);
        else if (!isLoading) setPromoProducts([]);
    }, [sample, isLoading]);

    // useEffect(() => {
    //     const mql = window.matchMedia('(pointer: fine) and (min-width: 768px)');
    //     const onChange = (e: MediaQueryListEvent | MediaQueryList) => setIsDesktop('matches' in e ? e.matches : (e as MediaQueryList).matches);
    //     onChange(mql);
    //     mql.addEventListener?.('change', onChange as (e: MediaQueryListEvent) => void);
    //     return () => mql.removeEventListener?.('change', onChange as (e: MediaQueryListEvent) => void);
    // }, []);

    const updateScrollBtns = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const EPS = 2;
        const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
        const scrollable = maxScrollLeft > EPS;
        setIsScrollable(scrollable);
        const atStart = el.scrollLeft <= EPS;
        const atEnd = Math.abs(maxScrollLeft - el.scrollLeft) <= EPS || el.scrollLeft + el.clientWidth >= el.scrollWidth - EPS;
        setCanLeft(!atStart);
        setCanRight(!atEnd);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollBtns();
        let raf = 0;
        const onScroll = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(updateScrollBtns);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        const onResize = () => updateScrollBtns();
        window.addEventListener('resize', onResize);
        return () => {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
            cancelAnimationFrame(raf);
        };
    }, [updateScrollBtns]);

    const scrollByAmount = useCallback(
        (dir: 'left' | 'right') => {
            const el = scrollRef.current;
            if (!el) return;
            const amount = Math.max(200, Math.floor(el.clientWidth * 0.8));
            el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
            // updateScrollBtns();
            requestAnimationFrame(updateScrollBtns);
            window.setTimeout(updateScrollBtns, 350);
        },
        [updateScrollBtns],
    );

    // const onNativeWheel = useCallback((e: WheelEvent) => {
    //     const el = scrollRef.current;
    //     if (!el) return;
    //     if (e.ctrlKey || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
    //     const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    //     const prev = el.scrollLeft;
    //     const next = Math.min(maxScrollLeft, Math.max(0, prev + e.deltaY));
    //     if (next !== prev) {
    //         el.scrollLeft = next;
    //         e.preventDefault();
    //     }
    // }, []);

    // useEffect(() => {
    //     const el = scrollRef.current;
    //     if (!el || !isDesktop) return;
    //     el.addEventListener('wheel', onNativeWheel as EventListener, { passive: false });
    //     return () => {
    //         el.removeEventListener('wheel', onNativeWheel as EventListener);
    //     };
    // }, [onNativeWheel, isDesktop]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const id1 = requestAnimationFrame(updateScrollBtns);
        const id2 = requestAnimationFrame(updateScrollBtns);
        let ro: ResizeObserver | null = null;
        if ('ResizeObserver' in window) {
            ro = new ResizeObserver(() => updateScrollBtns());
            ro.observe(el);
            Array.from(el.children).forEach((child) => ro!.observe(child as Element));
        }
        const mo = new MutationObserver(() => {
            if (ro) {
                Array.from(el.children).forEach((child) => ro!.observe(child as Element));
            }
            updateScrollBtns();
        });
        mo.observe(el, { childList: true });
        const imgs = Array.from(el.querySelectorAll('img'));
        const unsub: Array<() => void> = [];
        imgs.forEach((img) => {
            const i = img as HTMLImageElement;
            if (i.complete) return;
            const onDone = () => updateScrollBtns();
            i.addEventListener('load', onDone);
            i.addEventListener('error', onDone);
            unsub.push(() => {
                i.removeEventListener('load', onDone);
                i.removeEventListener('error', onDone);
            });
        });
        const onScroll = () => updateScrollBtns();
        el.addEventListener('scroll', onScroll, { passive: true });
        const onResize = () => updateScrollBtns();
        window.addEventListener('resize', onResize);
        return () => {
            cancelAnimationFrame(id1);
            cancelAnimationFrame(id2);
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
            unsub.forEach((fn) => fn());
            mo.disconnect();
            ro?.disconnect();
        };
    }, [promoProducts.length, updateScrollBtns]);

    const sortedPromoProducts = useMemo(() => {
        const norm = (v: number | undefined) => (Number.isFinite(v) && v! > 0 ? v! : Number.MAX_SAFE_INTEGER);
        return [...promoProducts].sort((a, b) => {
            const pa = norm(a.priority);
            const pb = norm(b.priority);
            if (pa !== pb) return pa - pb;
            // выше — более высокий discount.percent, затем — более высокая price (произвольно)
            const ad = a.discount?.percent ?? 0;
            const bd = b.discount?.percent ?? 0;
            if (ad !== bd) return bd - ad;
            return (b.price ?? 0) - (a.price ?? 0);
        });
    }, [promoProducts]);

    const shouldShowNav = isScrollable;
    if (promoProducts.length === 0) return null;
    return (
        <div className="promoProducts">
            <div className="promoProducts__titleBox">
                <h3 className="promoProducts__titleBox_title">Акции</h3>
                <div className="promoProducts__titleBox_controls">
                    <Action
                        as="link"
                        to={PATHS.discounts}
                        variant="outline"
                        extraClass="promoProducts__titleBox_controls_stocksLink promoProducts__titleBox_controls_stocksLink--desktop"
                    >
                        Смотреть всё
                    </Action>
                    {shouldShowNav && (
                        <>
                            <ActionLinkButton
                                onClick={() => scrollByAmount('left')}
                                disabled={!canLeft}
                                ariaLabel="Прокрутить влево"
                                iconAlt="Влево"
                                iconSrc="/icons/mobile/chevron-left.svg"
                                iconSrcDark="/icons/mobile/chevron-left_w.svg"
                                className="promoProducts__titleBox_controls_navBtn"
                            />
                            <ActionLinkButton
                                onClick={() => scrollByAmount('right')}
                                disabled={!canRight}
                                ariaLabel="Прокрутить вправо"
                                iconAlt="Вправо"
                                iconSrc="/icons/mobile/chevron-right.svg"
                                iconSrcDark="/icons/mobile/chevron-right_w.svg"
                                className="promoProducts__titleBox_controls_navBtn"
                            />
                        </>
                    )}
                </div>
            </div>
            {/* <div className="promoProducts__fade promoProducts__fade--left" aria-hidden />
            <div className="promoProducts__fade promoProducts__fade--right" aria-hidden /> */}
            {/* {shouldShowNav && (
                <>
                    <ActionLinkButton
                        onClick={() => scrollByAmount('left')}
                        disabled={!canLeft}
                        ariaLabel="Прокрутить влево"
                        iconAlt="Влево"
                        iconSrc="/icons/mobile/chevron-left.svg"
                        iconSrcDark="/icons/mobile/chevron-left_w.svg"
                        className="promoProducts__navMobBtn promoProducts__navMobBtn_left"
                    />
                    <ActionLinkButton
                        onClick={() => scrollByAmount('right')}
                        disabled={!canRight}
                        ariaLabel="Прокрутить вправо"
                        iconAlt="Вправо"
                        iconSrc="/icons/mobile/chevron-right.svg"
                        iconSrcDark="/icons/mobile/chevron-right_w.svg"
                        className="promoProducts__navMobBtn promoProducts__navMobBtn_right"
                    />
                </>
            )} */}
            <div ref={scrollRef} className={cardsClassName}>
                {/* <div className="promoProducts__fade promoProducts__fade--left" aria-hidden /> */}
                {/* <div className="promoProducts__fade promoProducts__fade--right" aria-hidden /> */}
                {sortedPromoProducts.map((card) => (
                    <ProductCard key={`promo-card-${card._id || card.slug}`} data={card} isMobile={isMobile} />
                ))}
                {/* <Link className="promoProducts__cards_linkElem" to={PATHS.showcase} aria-label="Перейти в каталог товаров и посмотреть все предложения">
                    <span className="promoProducts__cards_linkElem__inner">
                        <img className="promoProducts__cards_linkElem_img" src="/icons/link.svg" alt="" aria-hidden="true" />
                        <span className="promoProducts__cards_linkElem__title">Перейти в каталог</span>
                        <span className="promoProducts__cards_linkElem__subtitle">Смотреть все предложения интернет-магазина</span>
                    </span>
                </Link> */}
            </div>
            <Action
                as="link"
                to={PATHS.discounts}
                variant="outline"
                extraClass="promoProducts__titleBox_controls_stocksLink promoProducts__titleBox_controls_stocksLink--mobile"
            >
                Смотреть всё
            </Action>
        </div>
    );
};

export default PromoProducts;
